import { useState, useEffect } from 'react';
import { AppLayout } from '@/components/layout/AppLayout';
import { useProcurement } from '@/context/ProcurementContext';
import { formatCurrency } from '@/lib/utils';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  Package, 
  User, 
  Phone, 
  Calendar, 
  MapPin, 
  HandHeart,
  CheckCircle,
  Clock,
  FileText,
  TestTube
} from 'lucide-react';
import { toast } from 'sonner';

const FloorManagerDispatch = () => {
  const { state, actions } = useProcurement();
  const [showHandoverModal, setShowHandoverModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<any>(null);
  const [handoverData, setHandoverData] = useState({
    floorManagerName: '',
    floorManagerContact: '',
    handoverDateTime: new Date().toISOString().slice(0, 16), // Format for datetime-local input
    storageLocation: '',
    items: {
      invoice: false,
      physicalItems: false,
      packaging: false,
      documentation: false
    }
  });

  useEffect(() => {
    actions.fetchInvoices();
    actions.fetchHandoverLogs();
  }, []);

  // Watch for invoice changes to update the list
  useEffect(() => {
    console.log('Floor Manager Dispatch - Invoice state changed:', state.invoices.length, 'invoices');
  }, [state.invoices]);

  // Filter invoices based on invoice status after quality testing
  const readyForHandoverInvoices = state.invoices.filter(invoice => {
    // Show invoices that have completed quality testing and are ready for floor manager dispatch
    // Status: Fully Accepted (rating ≥ 5.0), Partly Accepted (rating 3.0-4.9)
    return (
      invoice.status === 'Fully Accepted' ||
      invoice.status === 'Partly Accepted'
    );
  });

  // Debug logging for Floor Manager Dispatch
  console.log('Floor Manager Dispatch - Total invoices:', state.invoices.length);
  console.log('Floor Manager Dispatch - Ready for handover:', readyForHandoverInvoices.length);
  console.log('Floor Manager Dispatch - Invoices:', readyForHandoverInvoices.map(inv => ({
    id: inv.id,
    invoiceNumber: inv.invoiceNumber,
    status: inv.status,
    qualityTested: inv.qualityTested,
    qualityRating: inv.qualityRating
  })));

  const handleHandoverClick = (invoice: any) => {
    setSelectedInvoice(invoice);
    setHandoverData({
      floorManagerName: '',
      floorManagerContact: '',
      handoverDateTime: new Date().toISOString().slice(0, 16),
      storageLocation: '',
      items: {
        invoice: false,
        physicalItems: false,
        packaging: false,
        documentation: false
      }
    });
    setShowHandoverModal(true);
  };

  const handleQualityTestClick = (invoice: any) => {
    // Navigate to Quality Management page for IQC testing
    // For now, show a toast message directing user to Quality Management
    toast.info(`Please go to Quality Management > IQC Ready to test invoice ${invoice.invoiceNumber}`);
  };

  const handleHandoverSubmit = async () => {
    if (!selectedInvoice || !handoverData.floorManagerName || !handoverData.floorManagerContact || !handoverData.storageLocation) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Items selection is now optional - no validation required

    try {
      // Create handover log
      const handoverLogData = {
        invoiceId: selectedInvoice.id,
        invoiceNumber: selectedInvoice.invoiceNumber,
        floorManagerName: handoverData.floorManagerName,
        floorManagerContact: handoverData.floorManagerContact,
        handoverDateTime: new Date(handoverData.handoverDateTime).toISOString(),
        storageLocation: handoverData.storageLocation,
        items: handoverData.items,
        status: 'Handed Over' as const
      };

      await actions.createHandoverLog(handoverLogData);

      // Update invoice with handover details but keep status as Fully Accepted/Partly Accepted for Quantity Testing
      await actions.updateInvoice(selectedInvoice.id, { 
        status: selectedInvoice.status, // Keep as Fully Accepted or Partly Accepted
        handedOverAt: new Date().toISOString(),
        handedOverBy: state.currentUser?.id || 1,
        handedOverByName: state.currentUser?.name || 'Floor Manager'
      });

      toast.success('Invoice handed over successfully');
      setShowHandoverModal(false);
      setSelectedInvoice(null);
    } catch (error) {
      console.error('Error handing over invoice:', error);
      toast.error('Failed to hand over invoice');
    }
  };


  const getStatusVariant = (status: string) => {
    switch (status) {
      case 'Approved':
        return 'default';
      case 'Received':
        return 'default';
      default:
        return 'outline';
    }
  };

  return (
    <AppLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Floor Manager Dispatch</h1>
            <p className="text-muted-foreground mt-2">
              Manage invoices through quality testing and handover to floor managers
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-sm">
              {readyForHandoverInvoices.length} Ready for Handover
            </Badge>
          </div>
        </div>

        {/* Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Ready for Handover</CardTitle>
              <Package className="h-4 w-4 text-blue-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{readyForHandoverInvoices.length}</div>
              <p className="text-xs text-muted-foreground">
                Invoices ready for processing
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Handed Over Today</CardTitle>
              <HandHeart className="h-4 w-4 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {state.handoverLogs.filter(log => {
                  const today = new Date().toDateString();
                  const logDate = new Date(log.handoverDateTime).toDateString();
                  return logDate === today;
                }).length}
              </div>
              <p className="text-xs text-muted-foreground">
                Completed handovers today
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Handovers</CardTitle>
              <CheckCircle className="h-4 w-4 text-purple-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{state.handoverLogs.length}</div>
              <p className="text-xs text-muted-foreground">
                All completed handovers
              </p>
            </CardContent>
          </Card>
        </div>


        {/* Invoices Ready for Handover */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Invoices Ready for Handover</CardTitle>
            <CardDescription>
              Invoices that have completed quality testing and are ready for handover (Fully Accepted, Partly Accepted)
            </CardDescription>
            <div className="flex gap-2 mt-2">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => {
                  actions.fetchInvoices();
                  console.log('Floor Manager Dispatch - Manual refresh triggered');
                }}
              >
                Refresh Data
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {readyForHandoverInvoices.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <div className="flex flex-col items-center gap-4">
                  <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center">
                    <Package className="h-8 w-8" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold">No Invoices Ready for Handover</h3>
                    <p className="text-sm">
                      Invoices with status "Fully Accepted" or "Partly Accepted" will appear here after quality testing
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Invoice ID</TableHead>
                    <TableHead>Vendor</TableHead>
                    <TableHead>Items</TableHead>
                    <TableHead>Quality Status</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {readyForHandoverInvoices.map((invoice) => {
                    const vendor = state.vendors.find(v => v.id === invoice.vendorId);
                    return (
                      <TableRow key={invoice.id}>
                        <TableCell className="font-medium">
                          <div className="flex flex-col">
                            <span>{invoice.invoiceNumber}</span>
                            <span className="text-xs text-muted-foreground">{invoice.poNumber}</span>
                          </div>
                        </TableCell>
                        <TableCell>{vendor?.name || invoice.vendorName}</TableCell>
                        <TableCell>
                          <div className="flex flex-col gap-1">
                            {invoice.items.slice(0, 2).map((item, index) => (
                              <div key={index} className="text-sm">
                                {item.skuName} (Qty: {item.quantity})
                              </div>
                            ))}
                            {invoice.items.length > 2 && (
                              <div className="text-xs text-muted-foreground">
                                +{invoice.items.length - 2} more items
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant={getStatusVariant(invoice.status)}>
                            {invoice.status}
                          </Badge>
                        </TableCell>
                        <TableCell>{formatCurrency(invoice.totalAmount)}</TableCell>
                        <TableCell>
                          {(() => {
                            const relatedPO = state.purchaseOrders.find(po => po.poNumber === invoice.poNumber);
                            const poStatus = relatedPO?.status;
                            
                            // Show "Hand Over" button for all PO statuses after quality testing
                            // FULLY_RECEIVED, PARTLY_RECEIVED, FULLY_REJECTED
                            return (
                              <Button
                                onClick={() => handleHandoverClick(invoice)}
                                size="sm"
                                className="flex items-center gap-2"
                              >
                                <HandHeart className="h-4 w-4" />
                                Hand Over
                              </Button>
                            );
                          })()}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Handover Modal */}
        <Dialog open={showHandoverModal} onOpenChange={setShowHandoverModal}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Hand Over Invoice</DialogTitle>
              <DialogDescription>
                Complete the handover details for invoice {selectedInvoice?.invoiceNumber}
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              {/* Floor Manager Name */}
              <div className="space-y-2">
                <Label htmlFor="floorManagerName" className="flex items-center gap-2">
                  <User className="h-4 w-4" />
                  Floor Manager Name *
                </Label>
                <Input
                  id="floorManagerName"
                  value={handoverData.floorManagerName}
                  onChange={(e) => setHandoverData(prev => ({ ...prev, floorManagerName: e.target.value }))}
                  placeholder="Enter floor manager name"
                />
              </div>

              {/* Contact Number */}
              <div className="space-y-2">
                <Label htmlFor="floorManagerContact" className="flex items-center gap-2">
                  <Phone className="h-4 w-4" />
                  Contact Number *
                </Label>
                <Input
                  id="floorManagerContact"
                  type="tel"
                  value={handoverData.floorManagerContact}
                  onChange={(e) => setHandoverData(prev => ({ ...prev, floorManagerContact: e.target.value }))}
                  placeholder="Enter contact number"
                />
              </div>

              {/* Date & Time */}
              <div className="space-y-2">
                <Label htmlFor="handoverDateTime" className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  Date & Time *
                </Label>
                <Input
                  id="handoverDateTime"
                  type="datetime-local"
                  value={handoverData.handoverDateTime}
                  onChange={(e) => setHandoverData(prev => ({ ...prev, handoverDateTime: e.target.value }))}
                />
              </div>

              {/* Storage Location */}
              <div className="space-y-2">
                <Label htmlFor="storageLocation" className="flex items-center gap-2">
                  <MapPin className="h-4 w-4" />
                  Storage/Assembly Location *
                </Label>
                <Select
                  value={handoverData.storageLocation}
                  onValueChange={(value) => setHandoverData(prev => ({ ...prev, storageLocation: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select storage location" />
                  </SelectTrigger>
                  <SelectContent>
                    {state.storageLocations.map((location) => (
                      <SelectItem key={location} value={location}>
                        {location}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Items to Hand Over */}
              <div className="space-y-3">
                <Label className="flex items-center gap-2">
                  <Package className="h-4 w-4" />
                  Items to Hand Over
                </Label>
                <p className="text-xs text-muted-foreground">
                  Select the items being handed over (optional)
                </p>
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="invoice"
                      checked={handoverData.items.invoice}
                      onCheckedChange={(checked) => 
                        setHandoverData(prev => ({
                          ...prev,
                          items: { ...prev.items, invoice: checked as boolean }
                        }))
                      }
                    />
                    <Label htmlFor="invoice" className="text-sm font-normal">
                      Invoice
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="physicalItems"
                      checked={handoverData.items.physicalItems}
                      onCheckedChange={(checked) => 
                        setHandoverData(prev => ({
                          ...prev,
                          items: { ...prev.items, physicalItems: checked as boolean }
                        }))
                      }
                    />
                    <Label htmlFor="physicalItems" className="text-sm font-normal">
                      Physical items
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="packaging"
                      checked={handoverData.items.packaging}
                      onCheckedChange={(checked) => 
                        setHandoverData(prev => ({
                          ...prev,
                          items: { ...prev.items, packaging: checked as boolean }
                        }))
                      }
                    />
                    <Label htmlFor="packaging" className="text-sm font-normal">
                      Packaging
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="documentation"
                      checked={handoverData.items.documentation}
                      onCheckedChange={(checked) => 
                        setHandoverData(prev => ({
                          ...prev,
                          items: { ...prev.items, documentation: checked as boolean }
                        }))
                      }
                    />
                    <Label htmlFor="documentation" className="text-sm font-normal">
                      Documentation
                    </Label>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setShowHandoverModal(false)}>
                  Cancel
                </Button>
                <Button onClick={handleHandoverSubmit} className="flex items-center gap-2">
                  <HandHeart className="h-4 w-4" />
                  Complete Handover
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

      </div>
    </AppLayout>
  );
};

export default FloorManagerDispatch;
